<template>
  <div class="home-banner-h5">
    <div ref="viewportRef" class="home-banner-h5__viewport">
      <div
        ref="trackRef"
        class="home-banner-h5__track"
        :class="{ 'home-banner-h5__track--animating': slideCount > 1 && transitionEnabled }"
        :style="trackStyle"
      >
        <button
          v-for="(slide, trackIndex) in trackSlides"
          :key="slide.key"
          type="button"
          class="home-banner-h5__slide"
          :style="slideStyle"
          :disabled="!getSlideComic(slide.slotMap) || slide.slideIndex !== displaySlideIndex || slide.isClone"
          @click="onSlideClick(slide.slotMap)"
        >
          <img
            v-if="getSlideComic(slide.slotMap) && shouldLoadSlideImages(trackIndex)"
            :src="getBannerImageUrl(getSlideComic(slide.slotMap), { layoutMode: 'h5' })"
            :alt="getBannerTitle(getSlideComic(slide.slotMap))"
            class="home-banner-h5__img"
            :loading="trackIndex === displayTrackIndex ? 'eager' : 'lazy'"
            decoding="async"
            :fetchpriority="trackIndex === displayTrackIndex ? 'high' : 'auto'"
          />
        </button>
      </div>
    </div>

    <div
      v-if="showDots"
      class="home-banner-h5__dots-layer"
    >
      <div
        class="home-banner-h5__dots"
        role="tablist"
        aria-label="轮播分页"
      >
        <button
          v-for="(_, index) in slideCount"
          :key="index"
          type="button"
          class="home-banner-h5__dot"
          :class="{ 'home-banner-h5__dot--active': index === displaySlideIndex }"
          :aria-label="`第 ${index + 1} 屏`"
          :aria-selected="index === displaySlideIndex"
          @pointerup.stop="onDotActivate(index, $event)"
          @keydown.enter.prevent="onDotActivate(index)"
          @keydown.space.prevent="onDotActivate(index)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useBannerSlideTrack } from '@/features/home/composables/useBannerSlideTrack'
import {
  BANNER_H5_POSITION,
  buildBannerTrackSlides,
  getBannerImageUrl,
  getBannerTitle,
} from '@/features/home/utils/bannerLink'

/** @typedef {Record<number, import('@/features/home/api/homeApi').HomeBannerComic|null>} BannerSlotMap */

const props = defineProps({
  slotMaps: {
    type: Array,
    required: true,
  },
  activeIndex: {
    type: Number,
    default: 0,
  },
  slideDirection: {
    type: String,
    default: 'auto',
  },
  slideCount: {
    type: Number,
    default: 0,
  },
  showDots: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['comic-click', 'go-to-slide'])

const viewportRef = ref(/** @type {HTMLElement|null} */ (null))
const trackRef = ref(/** @type {HTMLElement|null} */ (null))
const viewportWidth = ref(0)
const displayTrackIndex = ref(0)
const transitionEnabled = ref(true)

/** @type {ResizeObserver|null} */
let resizeObserver = null

const trackSlides = computed(() =>
  buildBannerTrackSlides(/** @type {BannerSlotMap[]} */ (props.slotMaps))
)

const {
  displaySlideIndex,
  animateToSlide,
  animateForward,
  animateBackward,
  syncAtRest,
  isAnimating,
  cleanup,
} = useBannerSlideTrack({
  trackRef,
  transitionEnabled,
  displayTrackIndex,
  getSlideCount: () => props.slideCount,
})

function updateViewportWidth() {
  viewportWidth.value = viewportRef.value?.clientWidth ?? 0
}

function bindResizeObserver() {
  unbindResizeObserver()
  updateViewportWidth()
  if (!viewportRef.value || typeof ResizeObserver === 'undefined') return
  resizeObserver = new ResizeObserver(updateViewportWidth)
  resizeObserver.observe(viewportRef.value)
}

function unbindResizeObserver() {
  resizeObserver?.disconnect()
  resizeObserver = null
}

onMounted(async () => {
  syncAtRest(props.activeIndex)
  await nextTick()
  bindResizeObserver()
})

onUnmounted(() => {
  unbindResizeObserver()
  cleanup()
})

watch(viewportRef, (el) => {
  if (!el) return
  bindResizeObserver()
})

watch(
  () => props.activeIndex,
  (curr, prev) => {
    if (prev === undefined) {
      syncAtRest(curr)
      return
    }
    if (curr === prev) return

    if (props.slideDirection === 'forward') {
      animateForward(prev, curr)
      return
    }
    if (props.slideDirection === 'backward') {
      animateBackward(prev, curr)
      return
    }
    animateToSlide(prev, curr)
  }
)

watch(
  () => props.slideCount,
  () => {
    if (!isAnimating()) syncAtRest(props.activeIndex)
  }
)

const slideStyle = computed(() => {
  if (viewportWidth.value <= 0) return {}
  return {
    width: `${viewportWidth.value}px`,
    flex: `0 0 ${viewportWidth.value}px`,
  }
})

const trackStyle = computed(() => {
  if (viewportWidth.value <= 0) return {}
  return {
    transform: `translate3d(-${displayTrackIndex.value * viewportWidth.value}px, 0, 0)`,
  }
})

/**
 * 仅加载当前屏及相邻屏图片（含 peek 克隆，保障循环动画）
 * @param {number} trackIndex
 * @returns {boolean}
 */
function shouldLoadSlideImages(trackIndex) {
  if (props.slideCount <= 1) return true
  const slide = trackSlides.value[trackIndex]
  // 真实屏常驻 DOM，避免 loop snap 时卸载再重建导致闪黑
  if (slide && !slide.isClone) return true
  // 循环动画终点克隆屏常驻，避免末屏→首屏动画时 lazy 解码滞后
  if (
    slide?.isClone &&
    (slide.key === 'clone-next' || slide.key === 'clone-prev')
  ) {
    return true
  }
  return Math.abs(trackIndex - displayTrackIndex.value) <= 2
}

/**
 * @param {BannerSlotMap} slotMap
 */
function getSlideComic(slotMap) {
  return slotMap?.[BANNER_H5_POSITION] ?? null
}

/**
 * @param {BannerSlotMap} slotMap
 */
function onSlideClick(slotMap) {
  const comic = getSlideComic(slotMap)
  if (!comic) return
  emit('comic-click', comic)
}

let lastDotActivateIndex = -1
let lastDotActivateAt = 0

/**
 * @param {number} index
 * @param {PointerEvent} [event]
 */
function onDotActivate(index, event) {
  if (event?.pointerType === 'mouse' && event.button !== 0) return

  const now = Date.now()
  if (index === lastDotActivateIndex && now - lastDotActivateAt < 400) return
  lastDotActivateIndex = index
  lastDotActivateAt = now

  emit('go-to-slide', index)
}
</script>

<style scoped src="@/features/home/styles/HomeBannerH5Track.css"></style>
